import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { getSupabaseBrowserClient } from "../integrations/supabase/client";
import { sourcedKey, useReadSource, viewRows } from "./readSource";

/**
 * The two availability reads behind Översikt's Webbplatsen box.
 *
 * There was a `/status` page until build round 4 (2026-09-27). plan.md section 5.6 since 2026-09-26:
 * "Status and incidents are in the website box on the overview; there is no status page." The
 * incident timeline it drew went with it, and so did the read of `incident_events`.
 *
 * Ordinary RLS-scoped selects. There is no client gateway for any of this and there should not be:
 * the client writes nothing here. Monitors are created by `admin_create_monitor`, incidents are opened
 * and closed by the dispatcher, and attribution is set by `admin_set_incident_attribution`.
 *
 * Each select names exactly the columns migration 6 grants to `authenticated`. The ungranted ones are
 * ungranted for a reason: `interval_seconds` and `retry_delay_seconds` are operating parameters, and
 * `private.monitor_checks` holds the individual failures. A select naming any of them would be refused
 * for the whole row, so the column list is part of the read rather than tidiness.
 */

export const monitorsKey = (tenantId: string) => ["portal", "monitors", tenantId] as const;
export const incidentsKey = (tenantId: string) => ["portal", "incidents", tenantId] as const;

export interface PortalMonitor {
  id: string;
  service_id: string;
  monitor_kind: string;
  endpoint_url: string;
  /** False means the monitor is not being checked at all, whatever `current_state` still says. */
  active: boolean;
  current_state: string;
  last_checked_at: string | null;
}

export interface PortalIncident {
  id: string;
  service_id: string;
  monitor_id: string;
  status: string;
  cause_attribution: string;
  detected_at: string;
  confirmed_at: string | null;
  recovered_at: string | null;
  /** Written by a person through `admin_set_incident_attribution`, never by the dispatcher. */
  client_summary: string | null;
}

export function useMonitors(tenantId: string | null): UseQueryResult<PortalMonitor[]> {
  const source = useReadSource();
  return useQuery({
    queryKey: sourcedKey(source, monitorsKey(tenantId ?? "none")),
    enabled: Boolean(tenantId),
    queryFn: async () => {
      if (source.kind === "admin") {
        return viewRows<PortalMonitor>("admin_as_client_monitors", { p_tenant_id: source.tenantId });
      }
      const supabase = getSupabaseBrowserClient();
      const { data, error } = await supabase
        .from("monitors")
        .select("id, service_id, monitor_kind, endpoint_url, active, current_state, last_checked_at")
        .order("monitor_kind", { ascending: true })
        .order("endpoint_url", { ascending: true });

      if (error) {
        throw error;
      }
      return (data ?? []) as PortalMonitor[];
    }
  });
}

/**
 * The newest incidents, newest first. Twenty, which reaches past the 90 days the overview shows for
 * any site that has not been down twenty times in a quarter, and an open incident is always among the
 * newest.
 */
export function useIncidents(tenantId: string | null): UseQueryResult<PortalIncident[]> {
  const source = useReadSource();
  return useQuery({
    queryKey: sourcedKey(source, incidentsKey(tenantId ?? "none")),
    enabled: Boolean(tenantId),
    queryFn: async () => {
      if (source.kind === "admin") {
        return viewRows<PortalIncident>("admin_as_client_incidents", { p_tenant_id: source.tenantId });
      }
      const supabase = getSupabaseBrowserClient();
      const { data, error } = await supabase
        .from("incidents")
        .select(
          "id, service_id, monitor_id, status, cause_attribution, detected_at, confirmed_at, recovered_at, client_summary"
        )
        .order("detected_at", { ascending: false })
        .limit(20);

      if (error) {
        throw error;
      }
      return (data ?? []) as PortalIncident[];
    }
  });
}
